import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import ScoreSelector from '../components/ScoreSelector';
import TextScoreSelector from '../components/TextScoreSelector';
import { useBreadEvaluation } from '../context/BreadEvaluationContext';
import { SaveIcon } from 'lucide-react';
const EvaluationPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    initializeEvaluation,
    updateScore,
    currentEvaluation,
    finalizeEvaluation
  } = useBreadEvaluation();
  const [sampleNumber, setSampleNumber] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [time, setTime] = useState(new Date().toTimeString().split(' ')[0].slice(0, 5));
  const [step, setStep] = useState(1);
  const handleStartEvaluation = () => {
    if (!sampleNumber || !date || !time) {
      alert('Por favor, preencha todos os campos.');
      return;
    }
    initializeEvaluation(sampleNumber, date, time);
    setStep(2);
  };
  const handleScoreChange = (attributeId: string, score: number) => {
    updateScore(attributeId, score);
  };
  const handleFinishEvaluation = () => {
    finalizeEvaluation();
    navigate('/results');
  };
  // Group attributes by category
  const getAttributesByCategory = () => {
    if (!currentEvaluation) return {};
    const grouped: Record<string, typeof currentEvaluation.attributes> = {};
    currentEvaluation.attributes.forEach(attr => {
      if (!grouped[attr.category]) {
        grouped[attr.category] = [];
      }
      grouped[attr.category].push(attr);
    });
    return grouped;
  };
  // Sensory attributes configurations
  const sensorySelectorOptions = {
    aroma: [{
      score: 0,
      description: 'sem aroma, queimado, essência manteiga, azedo'
    }, {
      score: 2,
      description: 'levemente com cheiro de queimado ou azedo'
    }, {
      score: 4,
      description: 'levemente sem aroma'
    }, {
      score: 6,
      description: 'levemente com cheiro de pão'
    }, {
      score: 8,
      description: 'cheiro agradável de pão'
    }, {
      score: 10,
      description: 'cheiro de pão intenso e característico'
    }],
    flavor: [{
      score: 0,
      description: 'azedo'
    }, {
      score: 2,
      description: 'massa crua'
    }, {
      score: 4,
      description: 'salgado'
    }, {
      score: 6,
      description: 'pouco salgado'
    }, {
      score: 8,
      description: 'Próximo do Ideal'
    }, {
      score: 10,
      description: 'Correto'
    }],
    resilience: [{
      score: 0,
      description: 'pão duro – ficou achatado'
    }, {
      score: 2,
      description: 'levemente duro – levemente amassado'
    }, {
      score: 4,
      description: 'indisponível para escolha',
      disabled: true
    }, {
      score: 6,
      description: 'formato voltou até a metade'
    }, {
      score: 8,
      description: 'ao pressionar, voltou lentamente ao formato original'
    }, {
      score: 10,
      description: 'pão voltou rapidamente ao formato'
    }]
  };
  const isSensoryAttribute = (id: string) => {
    return ['aroma', 'flavor', 'resilience'].includes(id);
  };
  const getSensorySelectorOptions = (attributeId: string) => {
    switch (attributeId) {
      case 'aroma':
        return sensorySelectorOptions.aroma;
      case 'flavor':
        return sensorySelectorOptions.flavor;
      case 'resilience':
        return sensorySelectorOptions.resilience;
      default:
        return [];
    }
  };
  const attributesByCategory = getAttributesByCategory();
  // Modificado: Habilitar o botão quando a pontuação total for maior que 2
  const isEvaluationComplete = (currentEvaluation?.totalScore || 0) > 2;
  return <div className="min-h-screen flex flex-col bg-gray-100" data-id="element-54">
      <Header data-id="element-55" />
      <main className="flex-grow container mx-auto px-4 py-8" data-id="element-56">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8" data-id="element-57">
          <h1 className="text-2xl font-bold text-gray-800 mb-6" data-id="element-58">
            Avaliação de Pão Tipo Francês
          </h1>
          {step === 1 ? <div data-id="element-59">
              <p className="text-gray-600 mb-6" data-id="element-60">
                Para iniciar a avaliação, preencha as informações abaixo sobre a
                amostra de pão a ser analisada.
              </p>
              <div className="space-y-4 max-w-md" data-id="element-61">
                <div data-id="element-62">
                  <label htmlFor="sampleNumber" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-63">
                    Número da amostra
                  </label>
                  <input type="text" id="sampleNumber" value={sampleNumber} onChange={e => setSampleNumber(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" placeholder="Ex: A001" data-id="element-64" />
                </div>
                <div data-id="element-65">
                  <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-66">
                    Data da coleta
                  </label>
                  <input type="date" id="date" value={date} onChange={e => setDate(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" data-id="element-67" />
                </div>
                <div data-id="element-68">
                  <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-69">
                    Horário da coleta
                  </label>
                  <input type="time" id="time" value={time} onChange={e => setTime(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500" data-id="element-70" />
                </div>
                <button onClick={handleStartEvaluation} className="mt-4 w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-4 rounded-md transition duration-200" data-id="element-71">
                  Iniciar Avaliação
                </button>
              </div>
            </div> : <div data-id="element-72">
              <div className="flex justify-between items-center mb-6" data-id="element-73">
                <div data-id="element-74">
                  <p className="text-sm text-gray-600" data-id="element-75">
                    <span className="font-medium" data-id="element-76">Amostra:</span>{' '}
                    {currentEvaluation?.sampleNumber}
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-77">
                    <span className="font-medium" data-id="element-78">Data/Hora:</span>{' '}
                    {currentEvaluation?.date} às {currentEvaluation?.time}
                  </p>
                </div>
                <div className="text-right" data-id="element-79">
                  <p className="text-sm text-gray-600" data-id="element-80">
                    <span className="font-medium" data-id="element-81">Pontuação atual:</span>{' '}
                    {currentEvaluation?.totalScore} / 130
                  </p>
                  <div className="w-48 bg-gray-200 rounded-full h-2.5 mt-1" data-id="element-82">
                    <div className="bg-orange-500 h-2.5 rounded-full" style={{
                  width: `${(currentEvaluation?.totalScore || 0) / 130 * 100}%`
                }} data-id="element-83"></div>
                  </div>
                </div>
              </div>
              <div className="space-y-8" data-id="element-84">
                {Object.entries(attributesByCategory).map(([category, attributes]) => <div key={category} className="bg-gray-50 p-6 rounded-lg border border-gray-200" data-id="element-85">
                      <h2 className="text-xl font-semibold text-gray-800 mb-4" data-id="element-86">
                        {category}
                      </h2>
                      {attributes.map(attr => isSensoryAttribute(attr.id) ? <TextScoreSelector key={attr.id} attributeId={attr.id} attributeName={attr.name} currentScore={attr.score} onChange={handleScoreChange} options={getSensorySelectorOptions(attr.id)} data-id="element-87" /> : <ScoreSelector key={attr.id} attributeId={attr.id} attributeName={attr.name} currentScore={attr.score} onChange={handleScoreChange} data-id="element-88" />)}
                    </div>)}
              </div>
              <div className="mt-8 flex justify-end" data-id="element-89">
                <button onClick={handleFinishEvaluation} disabled={!isEvaluationComplete} className={`flex items-center bg-orange-500 text-white font-bold py-2 px-6 rounded-md transition duration-200 ${isEvaluationComplete ? 'hover:bg-orange-600' : 'opacity-50 cursor-not-allowed'}`} data-id="element-90">
                  <SaveIcon className="mr-2" size={18} data-id="element-91" />
                  Finalizar Avaliação
                </button>
              </div>
            </div>}
        </div>
      </main>
    </div>;
};
export default EvaluationPage;